import React, { useMemo } from 'react';
import { useGetComplaintTrackingStatusQuery } from '../../redux/complaintApi';
import {
  formatComplaintStatusLabel,
  getComplaintStatusBadgeClass,
} from '../../utils/complaintStatus';
import {
  trackingEntriesFromApiResponse,
  trackingEntryDate,
  trackingStepPhase,
} from '../../utils/complaintTracking';

const getStatusClasses = (status) => getComplaintStatusBadgeClass(status);

const STEP_UI = {
  completed: {
    dot: 'bg-emerald-500 ring-2 ring-emerald-100',
    card: 'border-emerald-200 bg-emerald-50/80',
    label: 'Completed',
    labelClass: 'bg-emerald-100 text-emerald-800',
  },
  current: {
    dot: 'h-3.5 w-3.5 bg-brand ring-4 ring-brand/20',
    card: 'border-brand bg-brand/[0.07] shadow-sm ring-1 ring-brand/25',
    label: 'Current',
    labelClass: 'bg-brand text-white',
  },
  upcoming: {
    dot: 'bg-gray-200 ring-2 ring-white',
    card: 'border-gray-100 bg-gray-50 opacity-55',
    label: 'Not reached',
    labelClass: 'bg-gray-100 text-gray-500',
  },
};

export function ComplaintTrackingModal({ complaintId, open, onClose }) {
  const id = complaintId != null ? String(complaintId).trim() : '';
  const { data, isLoading, isError, error } = useGetComplaintTrackingStatusQuery(id, {
    skip: !open || !id,
  });

  const entries = useMemo(() => trackingEntriesFromApiResponse(data), [data]);
  const activeIndex = useMemo(
    () => entries.findIndex((e) => e?.active === true),
    [entries]
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-label="Complaint tracking"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div>
            <h3 className="text-lg font-semibold text-brand">Tracking Status</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-2xl leading-none text-red-700 hover:text-red-800"
            aria-label="Close tracking"
          >
            ×
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4">
          {isLoading ? (
            <p className="text-sm text-gray-500">Loading tracking details…</p>
          ) : isError ? (
            <p className="text-sm text-red-600">
              {error?.data?.message || error?.message || 'Could not load tracking status.'}
            </p>
          ) : entries.length === 0 ? (
            <p className="text-sm text-gray-500">No tracking history found.</p>
          ) : (
            <>
              <div className="mb-4 flex flex-wrap gap-2 text-[11px] font-medium">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-800">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" aria-hidden />
                  Completed
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-brand/10 px-2 py-0.5 text-brand">
                  <span className="h-2 w-2 rounded-full bg-brand" aria-hidden />
                  Current
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2 py-0.5 text-gray-500">
                  <span className="h-2 w-2 rounded-full bg-gray-300" aria-hidden />
                  Not reached yet
                </span>
              </div>

              <ol
                className={`relative space-y-4 border-l-2 pl-4 ${
                  activeIndex >= 0 ? 'border-brand/40' : 'border-gray-200'
                }`}
              >
                {entries.map((entry, idx) => {
                  const phase = trackingStepPhase(entries, idx);
                  const ui = STEP_UI[phase];
                  const statusLabel = formatComplaintStatusLabel(entry?.status);
                  const hasDate =
                    entry?.created_at != null && String(entry.created_at).trim() !== '';
                  const dateText =
                    phase === 'upcoming' && !hasDate
                      ? 'Not reached yet'
                      : trackingEntryDate(entry);
                  const dept =
                    entry?.accountable_department_name ??
                    entry?.department_name ??
                    entry?.to_department_name ??
                    '';
                  const by =
                    entry?.commented_by_name ??
                    entry?.account_name ??
                    entry?.created_by_name ??
                    '';
                  const key = entry?.id ?? `${idx}-${entry?.status}`;

                  return (
                    <li key={key} className="relative">
                      <span
                        className={`absolute -left-[1.4rem] top-2 h-2.5 w-2.5 rounded-full ${ui.dot}`}
                        aria-hidden
                      />
                      <div className={`rounded-lg border p-3 text-sm transition-colors ${ui.card}`}>
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${getStatusClasses(
                              statusLabel
                            )} ${phase === 'upcoming' ? 'opacity-70' : ''}`}
                          >
                            {statusLabel}
                          </span>
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${ui.labelClass}`}
                          >
                            {ui.label}
                          </span>
                          <span
                            className={`text-xs ${
                              phase === 'upcoming' && !hasDate
                                ? 'italic text-gray-400'
                                : 'text-gray-500'
                            }`}
                          >
                            {dateText}
                          </span>
                        </div>
                        {dept ? (
                          <p className="mt-2 text-xs text-gray-600">
                            <span className="font-medium text-gray-700">Department:</span> {dept}
                          </p>
                        ) : null}
                        {by ? (
                          <p className="mt-1 text-xs text-gray-600">
                            <span className="font-medium text-gray-700">By:</span> {by}
                          </p>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </>
          )}
        </div>

        <div className="border-t border-gray-100 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-lg border border-gray-200 bg-white py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default ComplaintTrackingModal;
